import * as d3 from 'd3';
import { Selection } from 'd3';
import { Geo } from '../../../utils/apollo/graphqlSchema';
import { assertCondition } from '../../../utils/types';
import {
  ICountyMapProperties,
  IStateMapProperties,
} from '../../../redux/slices/bootstrap';
import { IGeoMapTransform } from '../interfaces';
import { UnreachableCaseError } from 'ts-essentials';

export const GEO_MAP_HEIGHT = 400;
export const GEO_MAP_MIN_WIDTH = 500;
export const GEO_MAP_GROUP_CLASSNAME = 'geo-map-group';
export const GEO_MAP_TOOLTIP_PREFIX = 'tooltip-';
export const GEO_MAP_DATA_KEY_ATTRIBUTE = 'data-field-key';
export const GEO_MAP_PATH_CONTAINER = 'map-path-container';
export const GEO_MAP_AREA_LABEL_CLASSNAME = 'area-label-text';

// We would like to manually add labels for some states in the CB map,
// this can help us to manually adjust the position to deliver the best view for users
export const STATE_NEED_LABEL_IN_CB_MAP = ['UT', 'IA', 'MS', 'NY'];
export const STATE_NEED_LABEL_IN_CB_DIVISION_MAP = [
  'OR',
  'TX',
  'UT',
  'NE',
  'IN',
  'PA',
  'ME',
  'NC',
  'AL',
];

// assign zoom and pan action handler to zoom and return as ref
export const zoomAndPan = ({
  svg,
  transform,
  storeViewState,
}: {
  svg: Selection<SVGSVGElement, unknown, HTMLElement, unknown>;
  transform: IGeoMapTransform;
  storeViewState: (transform: IGeoMapTransform) => void;
}) => {
  let zoomEndTimeout: NodeJS.Timeout;

  const zoom = d3
    .zoom<SVGSVGElement, unknown>()
    .scaleExtent([1, 8])
    .on('zoom', (event) => {
      svg.select('g').attr('transform', event.transform);
    })
    .on('end', (event) => {
      // add a debounced function here to avoid calling storeViewState too frequently by triggering zoom by D3
      if (zoomEndTimeout) {
        clearTimeout(zoomEndTimeout);
      }
      zoomEndTimeout = setTimeout(() => {
        storeViewState(event.transform);
      }, 250);
    });

  svg.call(zoom);
  if (transform) {
    svg.call(
      zoom.transform,
      d3.zoomIdentity.translate(transform.x, transform.y).scale(transform.k),
    );
  }

  return zoom;
};

export const getKeyInGeoMapPropertiesByType = (
  geo: Geo,
): keyof ICountyMapProperties | keyof IStateMapProperties => {
  assertCondition(geo !== Geo.Na);
  switch (geo) {
    case Geo.UsCensusDivision:
      return 'CB_DIVISION_NAME';
    case Geo.UsState:
      return 'STATE_ABBR';
    case Geo.UsCensusRegion:
      return 'CB_REGION_NAME';
    case Geo.UsDma:
      return 'DMA_CODE';
    default:
      throw new UnreachableCaseError(geo);
  }
};

export const getMapContainerWidth = (containerId: string) => {
  let width = GEO_MAP_MIN_WIDTH;
  const svgNode = d3.select('#' + containerId).node();
  if (svgNode && svgNode instanceof Element) {
    width = Math.max(svgNode.getBoundingClientRect().width, width);
  }
  return width;
};

export const getContainerOffset = (containerId: string) => {
  const container = d3.select('#' + containerId);
  const rect = container.node();
  if (rect instanceof Element) {
    const boundingRect = rect.getBoundingClientRect();
    return {
      x: boundingRect.left,
      y: boundingRect.top,
    };
  }
  return { x: 0, y: 0 };
};
